import React from "react";
import { Box, List, ListItemButton, ListItemIcon, ListItemText, useTheme, Typography, alpha, } from "@mui/material";
import { NavLink, useLocation } from "react-router-dom";
import { centered, colCenter } from "@ui/components/style/style";
import { SButton } from "@ui/components/buttons/SButton";
import { LogoutCurve } from "iconsax-react";
import { SAvatar } from "@ui/components/avatar/SAvatar";
import { menuConfig } from "./menuConfig";
import { useAppSelector } from "@application/store/useStore";
import userService from "@utility/user-service";
import { PermissionChecker, Permission } from "@utility/permission-checker";
import LanguageSwitcher from "@ui/components/language-switcher/LanguageSwitcher";
import { useTranslation } from "react-i18next";
import TemperatureFilledIcon from "@ui/components/icons/TemperatureFilledIcon";
import { useFreeRoamingNodesSummary } from "@ui/hooks/useFreeRoamingNodesSummary";
export const DesktopSidebar = ({ onLogout }) => {
    var _a;
    const theme = useTheme();
    const location = useLocation();
    const { t } = useTranslation("common");
    const activeColor = theme.palette.primary.main;
    const inactiveColor = theme.palette.text.secondary;
    const { user } = useAppSelector((state) => state.auth);
    const userTest = user || userService.getUser();
    const userName = (user === null || user === void 0 ? void 0 : user.name) || userService.getUserDisplayName();
    const userEmail = (user === null || user === void 0 ? void 0 : user.email) || userService.getUserEmail();
    const isActive = (path) => {
        return (location.pathname === path || location.pathname.startsWith(path + "/"));
    };
    const getItemColor = (path) => {
        return isActive(path) ? theme.palette.primary.darker : inactiveColor;
    };
    // Map sidebar paths to required permissions
    const freeRoamingSummary = useFreeRoamingNodesSummary();
    const pathPermissions = {
        "/dashboard": Permission.DASHBOARD_VIEW,
        "/nodes": Permission.NODES_VIEW,
        "/free-roaming-nodes": Permission.NODES_VIEW,
        "/alarms": Permission.ALARMS_VIEW,
        "/insights": Permission.INSIGHTS_VIEW,
        "/reports": Permission.REPORTS_VIEW,
        "/users": Permission.USERS_VIEW,
        "/roles": Permission.ROLES_VIEW,
        "/clients": Permission.CLIENTS_VIEW,
        "/categories": Permission.CATEGORIES_VIEW,
        "/settings": Permission.SETTINGS_VIEW,
    };
    // Helper function to check if dashboard should be visible
    const canViewDashboard = () => {
        return (PermissionChecker.hasPermission(Permission.DASHBOARD_VIEW) ||
            PermissionChecker.hasPermission(Permission.ACCESSPOINTS_VIEW));
    };
    // Free Roaming Nodes: Super Admin only.
    const canViewFreeRoamingNodes = () => {
        if (!PermissionChecker.hasPermission(Permission.NODES_VIEW))
            return false;
        return freeRoamingSummary.isSuperAdmin;
    };
    // Calculate visible groups for the first section (without label)
    const firstGroupVisibleItems = ((_a = menuConfig[0]) === null || _a === void 0 ? void 0 : _a.items.filter((item) => {
        // Special handling for dashboard - show if user has Dashboard.View OR AccessPoints.View
        if (item.path === "/dashboard") {
            return canViewDashboard();
        }
        if (item.path === "/free-roaming-nodes") {
            return canViewFreeRoamingNodes();
        }
        const requiredPermission = pathPermissions[item.path];
        return (!requiredPermission ||
            PermissionChecker.hasPermission(requiredPermission));
    })) || [];
    const showSitexLabel = firstGroupVisibleItems.length > 0;
    return (React.createElement(Box, { component: "nav", sx: {
            width: 280,
            height: "100vh",
            px: 2,
            pb: 5,
            // backgroundColor: theme.palette.background.default,
            borderRight: `1px dashed ${theme.palette.divider}`,
            display: { xs: "none", lg: "flex" },
            flexDirection: "column",
            zIndex: theme.zIndex.drawer,
        } },
        React.createElement(Box, { component: "img", src: "/assets/images/logo.svg" }),
        React.createElement(Box, { component: "a", href: "https://sitexelectronics.com/insight/login/", target: "_blank", rel: "noopener noreferrer", sx: Object.assign(Object.assign({}, colCenter), { gap: 2, pl: 1.5, pr: 1, pt: 1.5, textDecoration: 'none', cursor: 'pointer' }) },
            React.createElement(TemperatureFilledIcon, null),
            React.createElement(Typography, { variant: "subtitle2", fontWeight: 500, color: theme.palette.primary.main }, "HACCP Application")),
        showSitexLabel && (React.createElement(Typography, { variant: "caption", sx: {
                color: theme.palette.text.disabled,
                fontWeight: 700,
                fontSize: 11,
                mb: 0.5,
                mt: 1.5,
                ml: 1,
                textTransform: "uppercase",
                display: "block",
            } }, "Sitex")),
        React.createElement(Box, { sx: { flexGrow: 1 } }, menuConfig
            .map((group, groupIndex) => {
            // Filter items by permissions first
            const visibleItems = group.items.filter((item) => {
                // Special handling for dashboard - show if user has Dashboard.View OR AccessPoints.View
                if (item.path === "/dashboard") {
                    return canViewDashboard();
                }
                if (item.path === "/free-roaming-nodes") {
                    return canViewFreeRoamingNodes();
                }
                const requiredPermission = pathPermissions[item.path];
                return (!requiredPermission ||
                    PermissionChecker.hasPermission(requiredPermission));
            });
            // Only render group if it has visible items
            if (visibleItems.length === 0)
                return null;
            return (React.createElement(Box, { key: group.label || groupIndex, sx: { mb: 2 } },
                group.label && (React.createElement(Typography, { variant: "caption", sx: {
                        color: theme.palette.text.disabled,
                        fontWeight: 700,
                        fontSize: 11,
                        mb: 1.5,
                        ml: 1,
                        display: "block",
                    } }, t(group.label))),
                React.createElement(List, { sx: { py: 0 } }, visibleItems.map((item) => (React.createElement(ListItemButton, { key: item.path, component: NavLink, to: item.path, disableRipple: true, selected: isActive(item.path), sx: {
                        mb: 0.5,
                        borderRadius: 3,
                        py: 1,
                        pl: 1,
                        pr: 1.5,
                        "&.Mui-selected": {
                            backgroundColor: alpha(activeColor, 0.08),
                            "&:hover": {
                                backgroundColor: alpha(activeColor, 0.12),
                            },
                        },
                    } },
                    React.createElement(ListItemIcon, { sx: {
                            color: getItemColor(item.path),
                            minWidth: 20,
                            mr: 2,
                        } }, item.icon(getItemColor(item.path))),
                    React.createElement(ListItemText, { primary: t(item.label), sx: {
                            "& .MuiTypography-root": {
                                color: getItemColor(item.path),
                                fontWeight: isActive(item.path) ? 600 : 500,
                                fontSize: 14,
                            },
                        } })))))));
        })
            .filter(Boolean)),
        React.createElement(Box, { sx: Object.assign(Object.assign({}, centered), { flexDirection: "column", gap: 1 }) },
            React.createElement(SAvatar, { size: 48, name: userName }),
            React.createElement(Box, { sx: { textAlign: "center" } },
                React.createElement(Typography, { variant: "subtitle1" }, userName),
                React.createElement(Typography, { variant: "caption", color: "textDisabled" }, userEmail)),
            React.createElement(Box, { sx: {
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    flexWrap: "nowrap",
                    width: "100%",
                    gap: 1,
                } },
                React.createElement(LanguageSwitcher, null),
                React.createElement(SButton, { variant: "soft", color: "error", fullWidth: true, sx: { borderRadius: 5 }, startIcon: React.createElement(LogoutCurve, null), onClick: onLogout }, t("sidebar.logout"))))));
};
